import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Select,
  SegmentedControl,
  Textarea,
  Checkbox,
  Alert,
  Divider,
  Badge,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBolt,
  IconInfoCircle,
  IconSparkles,
  IconTransfer,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api, type InitiateHandoverPayload } from '../lib/api';
import { useAuthStore } from '../stores/auth';

interface HandoverModalProps {
  opened: boolean;
  onClose: () => void;
  /** 'CW' for ConnectWise tickets, 'WECLAPP' for Weclapp tickets */
  source: 'CW' | 'WECLAPP';
  /** CW ticket ID (number) – required when source='CW' */
  cwTicketId?: number;
  /** Weclapp ticket ID (string) – required when source='WECLAPP' */
  weclappTicketId?: string;
  ticketSummary?: string;
  /**
   * Portal-User-ID des Empfängers zum Vorbelegen. Wird vom Kanban-Board genutzt: zieht ein
   * Techniker ein Ticket auf die Spalte einer anderen Person, ist die Absicht bereits klar —
   * dann soll nur noch die Begründung fehlen, nicht die erneute Auswahl.
   */
  initialToUserId?: string | null;
}

export function HandoverModal({
  opened,
  onClose,
  source,
  cwTicketId,
  weclappTicketId,
  ticketSummary,
  initialToUserId = null,
}: HandoverModalProps) {
  const queryClient = useQueryClient();

  // Disposition: ADMIN/TEAMLEAD dürfen ein Ticket direkt zuweisen, ohne Rücksprache und ohne
  // Bestätigung des Empfängers. Das ist kein neues Recht — dieselben Rollen können am Ticket
  // ohnehin frei zuweisen; hier passiert es nur dokumentiert (Kommentar + Benachrichtigung +
  // Eintrag in der Übergaben-Übersicht). Verbindlich ist die Prüfung im Backend.
  const role = useAuthStore((s) => s.user?.role);
  const mayDispatch = role === 'ADMIN' || role === 'TEAMLEAD';

  const [mode, setMode] = useState<'HANDOVER' | 'DISPATCH'>('HANDOVER');
  const isDispatch = mayDispatch && mode === 'DISPATCH';

  const [toUserId, setToUserId] = useState<string | null>(initialToUserId);

  // Beim Öffnen auf den vorgegebenen Empfänger setzen. Bewusst an `opened` gekoppelt, damit
  // ein erneutes Öffnen mit anderem Ziel nicht die alte Auswahl behält.
  useEffect(() => {
    if (opened) {
      setToUserId(initialToUserId);
      setMode('HANDOVER');
    }
  }, [opened, initialToUserId]);
  const [reason, setReason] = useState('');
  const [confirmedSpoken, setConfirmedSpoken] = useState(false);
  const [confirmedDocumented, setConfirmedDocumented] = useState(false);

  // Portal-Techniker aus DB (kein externer API-Aufruf nötig)
  const { data: techData, isLoading } = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 60_000,
    enabled: opened,
  });

  const technicianOptions = (techData?.technicians ?? [])
    .filter((t) =>
      source === 'CW'
        ? t.cwMemberIdentifier != null
        : t.weclappUserId != null
    )
    .filter((t) => !t.isCurrentUser)
    .map((t) => ({
      value: t.id,
      label: t.displayName,
    }));

  const systemLabel = source === 'CW' ? 'ConnectWise' : 'Weclapp';
  const noEligibleTechnicians = !isLoading && technicianOptions.length === 0;

  // KIAN formuliert die Übergabe-Begründung (HANDOVER-Modus: Grund, Stand, nötige
  // Infos für den Empfänger) — postet nichts, füllt nur das Feld.
  const kianReasonMutation = useMutation({
    mutationFn: () =>
      api.draftWeclappTicketComment(weclappTicketId!, {
        draft: reason.trim() || undefined,
        internal: true,
        usage: 'HANDOVER',
      }),
    onSuccess: (res) => setReason(res.text),
    onError: (e: Error) =>
      notifications.show({ title: 'KIAN-Entwurf fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const { mutate: initiateHandover, isPending } = useMutation({
    mutationFn: () => {
      const payload: InitiateHandoverPayload = isDispatch
        ? { toUserId: toUserId!, reason, ticketSummary, mode: 'DISPATCH' }
        : {
            toUserId: toUserId!,
            reason,
            ticketSummary,
            confirmedSpoken: true as const,
            confirmedDocumented: true as const,
          };
      if (source === 'WECLAPP' && weclappTicketId) {
        return api.initiateWeclappHandover(weclappTicketId, payload);
      }
      return api.initiateHandover(cwTicketId!, payload);
    },
    onSuccess: () => {
      notifications.show({
        title: isDispatch ? 'Ticket zugewiesen' : 'Übergabe eingeleitet',
        message: isDispatch
          ? 'Das Ticket ist sofort übertragen. Der Techniker wurde per E-Mail und Push informiert.'
          : 'Der Techniker wurde per E-Mail benachrichtigt und muss die Übergabe noch bestätigen.',
        color: 'green',
      });
      if (source === 'CW' && cwTicketId) {
        queryClient.invalidateQueries({ queryKey: ['ticket-handover-active', cwTicketId] });
        queryClient.invalidateQueries({ queryKey: ['ticket-timeline', cwTicketId] });
        queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', cwTicketId] });
      } else if (source === 'WECLAPP' && weclappTicketId) {
        queryClient.invalidateQueries({ queryKey: ['weclapp-handover-active', weclappTicketId] });
        queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', weclappTicketId] });
        // Der Vermerk zur Übergabe steht schon im Kommentar-Spiegel — ohne Neuladen fehlte er im Verlauf.
        queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', weclappTicketId] });
      }
      // Die Disposition ändert den Bearbeiter sofort — Listen und Boards zeigen sonst
      // weiter den alten Stand, bis sie von selbst nachladen.
      if (isDispatch) {
        queryClient.invalidateQueries({ queryKey: ['kanban-all-tickets'] });
        queryClient.invalidateQueries({ queryKey: ['my-kanban-tickets-all'] });
        queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
      }
      handleClose();
    },
    onError: (err: any) => {
      const message = err?.body?.error ?? err?.message ?? 'Unbekannter Fehler';
      notifications.show({
        title: isDispatch ? 'Fehler bei der Zuweisung' : 'Fehler beim Einleiten der Übergabe',
        message,
        color: 'red',
      });
    },
  });

  function handleClose() {
    setToUserId(null);
    setReason('');
    setConfirmedSpoken(false);
    setConfirmedDocumented(false);
    setMode('HANDOVER');
    onClose();
  }

  const canSubmit =
    toUserId !== null &&
    reason.trim().length >= 20 &&
    (isDispatch || (confirmedSpoken && confirmedDocumented));

  const ticketRef = source === 'CW' ? `#${cwTicketId}` : weclappTicketId;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          {isDispatch ? <IconBolt size={18} /> : <IconTransfer size={18} />}
          <Text fw={600}>{isDispatch ? 'Ticket disponieren' : 'Ticket übergeben'}</Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      zIndex={600}
    >
      <Stack gap="md">
        {/* Nur Admin/Teamlead sehen die Wahl — für Techniker bleibt alles wie bisher. */}
        {mayDispatch && (
          <SegmentedControl
            fullWidth
            value={mode}
            onChange={(v) => setMode(v as 'HANDOVER' | 'DISPATCH')}
            disabled={isPending}
            data={[
              { value: 'HANDOVER', label: 'Übergabe (mit Rücksprache)' },
              { value: 'DISPATCH', label: 'Disposition (ohne Rücksprache)' },
            ]}
          />
        )}

        <Alert
          icon={isDispatch ? <IconBolt size={16} /> : <IconInfoCircle size={16} />}
          color={isDispatch ? 'orange' : 'blue'}
          variant="light"
        >
          <Text size="sm">
            {isDispatch ? (
              <>
                Das Ticket wird <strong>sofort zugewiesen</strong> — ohne Rücksprache und ohne Bestätigung
                durch den Empfänger. Der Auftragstext geht als interner Kommentar ins Ticket und per
                E-Mail, Push und Glocke an den Techniker; ein bisher zuständiger Kollege wird ebenfalls
                informiert.
              </>
            ) : (
              <>
                Das Ticket wird <strong>erst übertragen</strong>, nachdem der Empfänger die Übergabe im Portal bestätigt hat.
                Beide Begründungen werden als interner Kommentar im Ticket gespeichert.
              </>
            )}
          </Text>
        </Alert>

        {ticketRef && (
          <Group gap="xs">
            <Text size="sm" c="dimmed">Ticket:</Text>
            <Badge variant="outline" color={source === 'WECLAPP' ? 'blue' : 'gray'} size="sm">
              {ticketRef}
            </Badge>
            {ticketSummary && (
              <Text size="sm" fw={500} style={{ flex: 1 }}>{ticketSummary}</Text>
            )}
          </Group>
        )}

        <Divider />

        <Select
          label={isDispatch ? 'Zuweisen an' : 'Übergeben an'}
          description={`Tippen, um zu suchen. Nur Techniker mit ${systemLabel}-Kennung können das Ticket übernehmen.`}
          placeholder={
            isLoading
              ? 'Wird geladen…'
              : noEligibleTechnicians
                ? 'Keine auswählbaren Techniker'
                : 'Techniker suchen oder auswählen…'
          }
          data={technicianOptions}
          value={toUserId}
          onChange={setToUserId}
          searchable
          clearable
          nothingFoundMessage="Kein passender Techniker gefunden"
          maxDropdownHeight={280}
          disabled={isPending || isLoading || noEligibleTechnicians}
          required
          leftSection={<IconArrowRight size={16} />}
          // Das Modal liegt auf zIndex 600, das Combobox-Dropdown nutzt ohne
          // Zutun Mantines Popover-Default (300) — es rendert dann HINTER dem
          // Modal und die Technikerliste wirkt leer. Muss über dem Modal liegen.
          comboboxProps={{ withinPortal: true, zIndex: 1000 }}
        />

        {noEligibleTechnicians && (
          <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
            <Text size="sm">
              Es sind keine Techniker mit hinterlegter <strong>{systemLabel}-Kennung</strong> verfügbar,
              an die dieses Ticket übergeben werden kann. Die {systemLabel}-Kennung muss im Benutzerprofil
              des Empfängers gepflegt sein.
            </Text>
          </Alert>
        )}

        <Textarea
          label={isDispatch ? 'Auftrag an den Techniker' : 'Begründung der Übergabe'}
          description={
            isDispatch
              ? 'Was ist zu tun und was muss der Techniker wissen? Dieser Text ersetzt die Rücksprache — er steht als interner Kommentar im Ticket und in der Benachrichtigung (mind. 20 Zeichen).'
              : 'Beschreibe kurz, warum die Übergabe nötig ist, und was der neue Techniker wissen muss (mind. 20 Zeichen).'
          }
          placeholder={
            isDispatch
              ? 'z. B. Bitte heute noch beim Kunden anrufen und den Vor-Ort-Termin für Donnerstag bestätigen. Ersatzteil liegt im Lager, Fach B3 — Details in den internen Notizen.'
              : 'z. B. Ich bin ab Montag im Urlaub. Der Kunde wartet auf Rückruf wegen dem VPN-Problem — Details habe ich in den internen Notizen festgehalten.'
          }
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={4}
          maxRows={8}
          autosize
          disabled={isPending}
          required
          error={
            reason.trim().length > 0 && reason.trim().length < 20
              ? 'Begründung zu kurz (mind. 20 Zeichen)'
              : undefined
          }
        />
        {/* KIAN formuliert die Begründung aus Ticketkontext + Stichpunkten aus —
            nur für Weclapp-Tickets (der Kontext-Endpoint ist Weclapp-seitig). */}
        {weclappTicketId && (
          <Group justify="flex-end" mt={-8}>
            <Button
              size="compact-xs"
              variant="light"
              color="grape"
              leftSection={<IconSparkles size={12} />}
              loading={kianReasonMutation.isPending}
              disabled={isPending}
              onClick={() => kianReasonMutation.mutate()}
            >
              {reason.trim() ? 'KIAN ausformulieren' : 'KIAN vorschlagen'}
            </Button>
          </Group>
        )}

        {!isDispatch && (
          <>
        <Divider label="Pflichtbestätigungen" labelPosition="left" />

        <Stack gap="sm">
          <Checkbox
            checked={confirmedSpoken}
            onChange={(e) => setConfirmedSpoken(e.currentTarget.checked)}
            disabled={isPending}
            label={
              <Text size="sm">
                Ich habe <strong>bereits persönlich mit dem Techniker gesprochen</strong> und ihm alle nötigen Informationen zum Ticket weitergegeben.
              </Text>
            }
          />
          <Checkbox
            checked={confirmedDocumented}
            onChange={(e) => setConfirmedDocumented(e.currentTarget.checked)}
            disabled={isPending}
            label={
              <Text size="sm">
                Alle relevanten Informationen zur Übergabe sind <strong>schriftlich in den Ticket-Kommentaren</strong> durch mich oder den Techniker festgehalten.
              </Text>
            }
          />
        </Stack>
          </>
        )}

        {!canSubmit && (confirmedSpoken || confirmedDocumented || reason || toUserId) && (
          <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
            <Text size="sm">
              {isDispatch
                ? 'Bitte Techniker auswählen und den Auftrag beschreiben (mind. 20 Zeichen).'
                : 'Bitte fülle alle Pflichtfelder aus und bestätige beide Punkte, bevor die Übergabe eingeleitet werden kann.'}
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={handleClose} disabled={isPending}>
            Abbrechen
          </Button>
          <Button
            leftSection={isDispatch ? <IconBolt size={16} /> : <IconTransfer size={16} />}
            onClick={() => initiateHandover()}
            disabled={!canSubmit}
            loading={isPending}
            color="orange"
          >
            {isDispatch ? 'Ticket zuweisen' : 'Übergabe einleiten'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
